import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const DashboardLogin = () => {
  const [formData, setFormData] = useState({
    loginId: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttempted = useRef(false);
  const { login, dashboardUser, loading: authLoading } = useAuth();

  const hasDashboardAccess = (userOrRole) => {
    const role = (typeof userOrRole === 'string' ? userOrRole : userOrRole?.role || '').toLowerCase();
    const roleName = String(
      typeof userOrRole === 'string'
        ? ''
        : userOrRole?.role_name || userOrRole?.role_display_name || ''
    ).toLowerCase();
    const roleLevel = Number(typeof userOrRole === 'string' ? 0 : userOrRole?.role_level || 0);
    return role.includes('admin') || role === 'employee' || roleName.includes('super admin') || roleLevel >= 10;
  };

  // Only redirect if user is already authenticated as admin/employee
  // Wait for auth to finish loading before checking
  useEffect(() => {
    console.log('🔍 [DashboardLogin] Auth check:', { 
      authLoading, 
      dashboardUserRole: dashboardUser?.role,
      pathname: location.pathname,
      redirectAttempted: redirectAttempted.current
    });
    
    // Prevent multiple redirect attempts
    if (redirectAttempted.current) {
      console.log('⏸️ [DashboardLogin] Redirect already attempted, skipping');
      return;
    }
    
    // Don't do anything while auth is still loading
    if (authLoading) {
      console.log('⏳ [DashboardLogin] Auth still loading, waiting...');
      return;
    }

    // Once auth is loaded, check if user should be redirected
    // Check dashboard user specifically (not e-commerce user)
    if (dashboardUser && hasDashboardAccess(dashboardUser)) {
      console.log('✅ [DashboardLogin] User already authenticated as admin/employee, redirecting to dashboard');
      redirectAttempted.current = true;
      navigate('/dashboard', { replace: true });
    } else {
      // Mark auth as checked so we can show the login form
      console.log('✅ [DashboardLogin] Auth checked, showing login form. Dashboard user:', dashboardUser?.role || 'not authenticated');
      setAuthChecked(true);
    }
  }, [dashboardUser, navigate, authLoading, location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Make direct API call to check user role before logging in
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const loginUrl = API_BASE ? `${API_BASE}/api/auth/login` : '/api/auth/login';
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const userData = data.data?.user || data.user;
        const token = data.data?.token || data.token;

        if (!userData || !token) {
          throw new Error('Invalid response format from server');
        }

        // Check if user has dashboard access (admin, employee)
        if (hasDashboardAccess(userData)) {
          // Use AuthContext login with token and user - dashboard login
          const authResult = await login(token, userData, true);
          if (authResult.success) {
            toast.success('Login successful!');
            // Use replace to prevent back button issues
            navigate('/dashboard', { replace: true });
          } else {
            toast.error(authResult.message || 'Failed to authenticate');
          }
        } else {
          toast.error('You do not have access to the dashboard. Admin or Employee access required.');
          // Clear form on error
          setFormData({ loginId: '', password: '' });
        }
      } else {
        toast.error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Check if it's a network error
      if (error.message === 'Failed to fetch' || error.message.includes('NetworkError') || error.name === 'TypeError' || error.message.includes('Network error')) {
        toast.error('Network error: Unable to connect to server. Please check if the server is running on port 3000.');
      } else {
        toast.error(error.message || 'An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (authLoading || !authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't use Layout - this is completely separate
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Login</h1>
            <p className="text-gray-600 mt-2">Enter your credentials to access the dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="loginId" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="loginId"
                type="email"
                required
                value={formData.loginId}
                onChange={(e) => setFormData({ ...formData, loginId: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Access restricted to authorized personnel only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLogin;

